<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="flex space-x-2 element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('HeadContract.CashflowForecasting.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.CashflowForecasting.subTitle') }}
            </p>
          </div>
        </div>

        <div
          class="element-island flex items-center gap-3 flex-wrap justify-end"
        >
          <div
            v-if="contractAmount.adjustments !== 0"
            class="flex flex-col"
          >
            <div
              class="flex justify-between space-x-2"
            >
              <span class="text-sm font-medium text-gray-900">
                Gross Works Value
              </span>

              <span class="text-sm text-gray-900">
                {{ $tcc(contractAmount.work) }}
              </span>
            </div>

            <div

              class="flex justify-between space-x-2"
            >
              <span class="text-sm font-medium text-gray-900">
                Contract Adjustments
              </span>

              <span class="text-sm text-gray-900">
                {{ $tcc(-contractAmount.adjustments) }}
              </span>
            </div>

            <div class="flex justify-between space-x-2">
              <span class="text-sm font-medium text-gray-900">
                Contract Sum
              </span>

              <span class="text-sm text-gray-900">
                {{ $tcc(contractAmount.total) }}
              </span>
            </div>
          </div>

          <div
            v-else
            class="bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm"
          >
            <div class="text-sm text-gray-500">
              {{ $t('HeadContract.CashFlow.contractSum') }}
            </div>

            <div class="text-lg font-semibold text-gray-900">
              {{ $tcc(contractAmount.total) }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="element-island overflow-y-auto flex flex-col min-h-max flex-1"
      >
        <div
          class="w-full duration-200 transition-[height] ease-linear"
          style="--animate-duration: 0.2s;"
          :class="[showGraphs ? 'h-96' : 'h-0']"
          @transitionend="graphsHidden"
        >
          <Transition
            enter-active-class="animate__animated animate__fadeIn"
            leave-active-class="animate__animated animate__fadeOut"
          >
            <div
              v-show="showGraphsCanvas"
              class="flex w-full h-full relative"
            >
              <CashflowChart
                ref="chartRef"
                :project-id="projectId"
              />
            </div>
          </Transition>
        </div>

        <div class="w-full relative overflow-y-auto flex-1 rounded-md border border-gray-200">
          <div
            class="sticky z-[25] left-2 top-4 w-min"
          >
            <button
              type="button"
              class="group p-1 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              @click="toggleGraph"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="showGraphs"
               
                class="w-5 h-5 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>

              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-else
               
                class="w-5 h-5 group-hover:stroke-ccm-blue-500 cursor-pointer">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>
            </button>
          </div>

          <div class="flex flex-col absolute inset-0">
            <TradeForecastRow
              v-for="trade in workTrades"
              :key="trade.id"
              :trade="trade"
              @forecast-updated="updateForecast(trade, $event.newForecasts)"
              @forecast-saved="chartRef?.clearForecast(trade)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import CashflowChart from '@/components/HeadContract/CashflowForecasting/CashflowChart.vue';
import TradeForecastRow from '@/components/HeadContract/CashflowForecasting/TradeForecastRow.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import Trade from '@/models/Trade';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

useBoqLockRedirect();

const tradesStore = useTradesStore();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);

watch(() => props.projectId, () => {
  fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
    console.log(error);
  });
}, { immediate: true });

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items', (query) => {
      query.whereNull('parent_id');
    })
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', 'direct')
    .orderBy('sort_order')
    .whereHas('trade_items')
    .get();
});

const workTrades = computed(() => {
  return trades.value.filter((trade) => trade.totalBudget > 0);
});

const contractAmount = computed(() => {
  const contractAmount = {
    total: 0,
    work: 0,
    adjustments: 0,
  };

  trades.value.forEach((trade) => {
    contractAmount.total += trade.totalAdjustedBudget;
    contractAmount.work += trade.totalBudget;
    contractAmount.adjustments += trade.totalBudget - trade.totalAdjustedBudget;
  });

  return contractAmount;
});

const chartRef = useTemplateRef('chartRef');
const showGraphs = ref(true);
const showGraphsCanvas = ref(true);
const setFullHeight = ref(false);

const toggleGraph = () => {
  if(showGraphs.value) {
    showGraphsCanvas.value = false;
    showGraphs.value = false;
    setFullHeight.value = true;
  } else {
    if(setFullHeight.value) {
      showGraphs.value = true;
      setFullHeight.value = false;
    }
  }
};

const graphsHidden = () => {
  if(showGraphs.value) {
    showGraphsCanvas.value = true;
  } else {
    showGraphsCanvas.value = false;
  }
};

const updateForecast = (trade: Trade, newForecasts: number[]) => {
  chartRef.value.updateForecastOverride(trade, newForecasts);
};
</script>

<style scoped></style>
